import { OkResponse, SessionResponse, type User } from '@squats/shared';
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { api, ApiError, isOffline } from './api.ts';
import { clearLocal } from './log/db.ts';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signedOut'; offline: boolean }
  | { status: 'signedIn'; user: User; offline: boolean };

type AuthContextValue = {
  state: AuthState;
  /** After a passkey sign-in or sign-up, or a profile change. */
  setUser: (user: User) => void;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

// The last signed-in user, so the app still opens at the gym with no signal
// (docs/SCOPE.md, Offline). Only profile basics; the session itself is an
// HttpOnly cookie the page can't read.
const CACHE_KEY = 'squats.user';

function readCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(CACHE_KEY);
    return raw ? (JSON.parse(raw) as User) : null;
  } catch {
    return null;
  }
}

function writeCachedUser(user: User | null) {
  try {
    if (user) localStorage.setItem(CACHE_KEY, JSON.stringify(user));
    else localStorage.removeItem(CACHE_KEY);
  } catch {
    // Storage unavailable (private mode etc.): offline start just won't work.
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  const refresh = useCallback(async () => {
    try {
      const { user } = await api('/api/auth/session', SessionResponse);
      writeCachedUser(user);
      setState(user ? { status: 'signedIn', user, offline: false } : { status: 'signedOut', offline: false });
    } catch (e) {
      // Can't reach the server, or it's restarting (a deploy): stay signed in on the saved profile. Anything
      // else (a real answer that isn't a session) is signed out.
      const unavailable = isOffline(e) || (e instanceof ApiError && e.status >= 500);
      if (!unavailable) {
        setState({ status: 'signedOut', offline: false });
        return;
      }
      const cached = readCachedUser();
      setState(cached ? { status: 'signedIn', user: cached, offline: true } : { status: 'signedOut', offline: true });
      // A restarting server is back in seconds; no "online" event will fire for it, so check again.
      if (!isOffline(e)) setTimeout(() => void refresh(), 5_000);
    }
  }, []);

  useEffect(() => {
    void refresh();
    window.addEventListener('online', refresh);
    return () => window.removeEventListener('online', refresh);
  }, [refresh]);

  const setUser = useCallback((user: User) => {
    writeCachedUser(user);
    setState({ status: 'signedIn', user, offline: false });
  }, []);

  const signOut = useCallback(async () => {
    await api('/api/auth/logout', OkResponse, { body: {} }).catch(() => {});
    writeCachedUser(null);
    // Workouts and saved data on this device belong to the person signing out.
    await clearLocal().catch(() => {});
    setState({ status: 'signedOut', offline: false });
  }, []);

  const value = useMemo(() => ({ state, setUser, signOut }), [state, setUser, signOut]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}

/** The signed-in user. Only for screens rendered behind sign-in. */
export function useUser(): User {
  const { state } = useAuth();
  if (state.status !== 'signedIn') throw new Error('useUser while signed out');
  return state.user;
}
